import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2Icon, PlusIcon } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  useCreateFlowMutation,
  useFlowTypeListQuery,
  useIsFeatureEnabled,
  useSetTagsForEntityMutation,
} from '../../../services/api';
import { useNotificationsProvider } from '../../../context/NotificationsContext';
import { FlowTypes } from '../../../entities/flow-type';
import { Features } from '../../../entities/features';
import DynamicFieldSelectorDialog from '../../dynamic-field-selector';
import DynamicFieldValueComponent from '../../dynamic-field-value';
import { TagsMultiSelect } from '../../tags-multi-select';

AddFlowDialog.propTypes = {
  title: PropTypes.string,
  open: PropTypes.bool,
  onClose: PropTypes.func,
  onCreated: PropTypes.func,
};

export default function AddFlowDialog({ title, open, onClose, onCreated }) {
  const { t } = useTranslation();
  const { showNotification } = useNotificationsProvider();

  const { data: flowTypes } = useFlowTypeListQuery();
  const [createFlow, { isLoading: isCreating }] = useCreateFlowMutation();
  const [setTags, { isLoading: isSettingTags }] = useSetTagsForEntityMutation();

  const [openDynamicFieldSelector, setOpenDynamicFieldSelector] = useState(false);
  const [selectedDynamicField, setSelectedDynamicField] = useState(null);
  const [flowTypesList, setFlowTypesList] = useState([]);

  const isSignatureFeatureEnabled = useIsFeatureEnabled(Features.FT_INSPECTION_SIGNATURES);
  const isResourceEvaluationFormFeatureEnabled = useIsFeatureEnabled(Features.FT_RESOURCE_EVALUATION_FORM);

  const loading = isCreating || isSettingTags;

  const schema = z
    .object({
      flowTitle: z.string().trim().min(1, t('flows.errors.title-required', 'Title is required')),
      type: z
        .string()
        .min(1, t('flows.errors.type-required', 'Type is required'))
        .refine((v) => v !== 'none', t('flows.errors.type-required', 'Type is required')),
      selectedTags: z.array(z.any()),
      hasSignatures: z.boolean(),
      signaturesCount: z.number(),
      signatureOneTitle: z.string(),
      signatureTwoTitle: z.string(),
      isResourceEvaluationFormActive: z.boolean(),
      dynamicFields: z.array(z.any()),
    })
    .superRefine((v, ctx) => {
      if (v.hasSignatures) {
        if (v.signatureOneTitle.length === 0) {
          ctx.addIssue({
            code: 'custom',
            path: ['signatureOneTitle'],
            message: t('flows.add-flow-dialog.signature-title-missing'),
          });
        }
        if (v.signaturesCount === 2 && v.signatureTwoTitle.length === 0) {
          ctx.addIssue({
            code: 'custom',
            path: ['signatureTwoTitle'],
            message: t('flows.add-flow-dialog.signature-titles-missing'),
          });
        }
      }
    });

  const form = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      flowTitle: '',
      type: '',
      selectedTags: [],
      hasSignatures: false,
      signaturesCount: 1,
      signatureOneTitle: '',
      signatureTwoTitle: '',
      isResourceEvaluationFormActive: false,
      dynamicFields: [],
    },
  });

  const resetState = () => {
    form.reset();
    setSelectedDynamicField(null);
  };

  const onOpenChange = (next) => {
    if (!next) {
      if (loading) return;
      resetState();
      onClose?.();
    }
  };

  const watchHasSignatures = form.watch('hasSignatures');
  const watchSignaturesCount = form.watch('signaturesCount');
  const watchEvalFormActive = form.watch('isResourceEvaluationFormActive');
  const watchDynamicFields = form.watch('dynamicFields');

  const onFieldAdded = (data) => {
    setOpenDynamicFieldSelector(false);
    const current = form.getValues('dynamicFields');
    if (selectedDynamicField) {
      const next = current.map((field) =>
        field === selectedDynamicField ? data : field,
      );
      form.setValue('dynamicFields', next);
      setSelectedDynamicField(null);
      return;
    }
    form.setValue('dynamicFields', [...current, data]);
  };

  const onEditField = (data) => {
    setSelectedDynamicField(data);
    setOpenDynamicFieldSelector(true);
  };

  const onDeleteField = (data) => {
    const current = form.getValues('dynamicFields');
    form.setValue('dynamicFields', current.filter((field) => field !== data));
  };

  const onSubmit = async (values) => {
    const data = {
      name: values.flowTitle.trim(),
      flowTypeId: values.type,
      hasSignatures: values.hasSignatures,
      isResourceEvaluationFormActive: values.isResourceEvaluationFormActive,
    };
    if (values.hasSignatures) {
      data.signatureTitles = values.signaturesCount === 1
        ? [values.signatureOneTitle]
        : [values.signatureOneTitle, values.signatureTwoTitle];
    }
    if (values.isResourceEvaluationFormActive) {
      data.evaluationFormDynamicFields = values.dynamicFields;
    }

    try {
      const created = await createFlow(data).unwrap();
      if (values.selectedTags.length > 0 && created?.id) {
        await setTags({
          taggableType: 'Flow',
          taggableId: created.id,
          tagIds: values.selectedTags.map((tag) => tag.id),
        }).unwrap();
      }
      resetState();
      onCreated?.();
    } catch (err) {
      const errorCode = err?.data?.validationErrors?.[0]?.errorCode;
      if (errorCode === 'FLOW_DETAILS_ALREADY_USED') {
        showNotification('error', t('flows.add-flow-dialog.already-exists-error'));
      } else if (errorCode === 'FLOW_SIGNATURE_TITLES_MISSING') {
        showNotification(
          'error',
          values.signaturesCount === 1
            ? t('flows.add-flow-dialog.signature-title-missing')
            : t('flows.add-flow-dialog.signature-titles-missing'),
        );
      } else {
        showNotification('error', t('common.api-error'));
      }
    }
  };

  useEffect(() => {
    if (flowTypes) {
      setFlowTypesList(flowTypes.flowTypes.map((flowType) => {
        if (flowType.name === FlowTypes.FREE) {
          return { id: flowType.id, title: t('flows.add-flow-dialog.select-type.free') };
        }
        if (flowType.name === FlowTypes.SEQUENTIAL) {
          return { id: flowType.id, title: t('flows.add-flow-dialog.select-type.sequencial') };
        }
        return { id: flowType.id, title: flowType.name };
      }));
    }
  }, [flowTypes, t]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-h-[90vh] overflow-y-auto sm:max-w-lg"
        onInteractOutside={(e) => { if (loading) e.preventDefault(); }}
        onEscapeKeyDown={(e) => { if (loading) e.preventDefault(); }}
      >
        <DialogHeader>
          <DialogTitle>{title ?? t('flows.add-flow-dialog.title')}</DialogTitle>
          <DialogDescription className="sr-only">
            {t('flows.add-flow-dialog.title')}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="space-y-5"
            noValidate
          >
            <FormField
              control={form.control}
              name="flowTitle"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('flows.add-flow-dialog.flow-title')}</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="type"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('flows.add-flow-dialog.type')}</FormLabel>
                  <Select value={field.value} onValueChange={field.onChange}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder={t('flows.add-flow-dialog.type')} />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {flowTypesList.map((flowType) => (
                        <SelectItem key={flowType.id} value={String(flowType.id)}>
                          {flowType.title}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="selectedTags"
              render={({ field }) => (
                <FormItem>
                  <TagsMultiSelect
                    value={field.value}
                    onChange={field.onChange}
                    label={t('flows.add-flow-dialog.tags-title')}
                    placeholder={t('tags.multi-select.placeholder')}
                  />
                  <FormMessage />
                </FormItem>
              )}
            />

            {isSignatureFeatureEnabled ? (
              <div className="space-y-3 border-t border-border pt-4">
                <FormField
                  control={form.control}
                  name="hasSignatures"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-center gap-2 space-y-0">
                      <FormControl>
                        <Checkbox
                          checked={field.value}
                          onCheckedChange={(v) => field.onChange(Boolean(v))}
                        />
                      </FormControl>
                      <span className="text-sm">
                        {t('flows.add-flow-dialog.add-signatures')}
                      </span>
                    </FormItem>
                  )}
                />
                {watchHasSignatures ? (
                  <div className="space-y-3 pl-6">
                    <FormField
                      control={form.control}
                      name="signaturesCount"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>
                            {t('flows.add-flow-dialog.signatures-number')}
                          </FormLabel>
                          <Select
                            value={String(field.value)}
                            onValueChange={(v) => field.onChange(Number(v))}
                          >
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectItem value="1">1</SelectItem>
                              <SelectItem value="2">2</SelectItem>
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="signatureOneTitle"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>
                            {t('flows.add-flow-dialog.signature-one-title')}
                          </FormLabel>
                          <FormControl>
                            <Input {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    {watchSignaturesCount === 2 ? (
                      <FormField
                        control={form.control}
                        name="signatureTwoTitle"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>
                              {t('flows.add-flow-dialog.signature-two-title')}
                            </FormLabel>
                            <FormControl>
                              <Input {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    ) : null}
                  </div>
                ) : null}
              </div>
            ) : null}

            {isResourceEvaluationFormFeatureEnabled ? (
              <div className="space-y-3 border-t border-border pt-4">
                <FormField
                  control={form.control}
                  name="isResourceEvaluationFormActive"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-center gap-2 space-y-0">
                      <FormControl>
                        <Checkbox
                          checked={field.value}
                          onCheckedChange={(v) => field.onChange(Boolean(v))}
                        />
                      </FormControl>
                      <span className="text-sm">
                        {t('flows.add-flow-dialog.enable-resource-evaluation-form')}
                      </span>
                    </FormItem>
                  )}
                />
                {watchEvalFormActive ? (
                  <div className="space-y-3 pl-6">
                    {watchDynamicFields.map((field) => (
                      <DynamicFieldValueComponent
                        key={`${field.title}-${field.dataType}`}
                        value={field}
                        onEdit={onEditField}
                        onDelete={onDeleteField}
                        titleOnly
                      />
                    ))}
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="text-primary"
                      onClick={() => setOpenDynamicFieldSelector(true)}
                    >
                      <PlusIcon className="size-4" />
                      {t('resource.add-resource-dialog.add-field')}
                    </Button>
                  </div>
                ) : null}
              </div>
            ) : null}

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={loading}
              >
                {t('common.cancel')}
              </Button>
              <Button type="submit" disabled={loading}>
                {loading ? <Loader2Icon className="mr-2 size-4 animate-spin" /> : null}
                {t('common.save')}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>

      <DynamicFieldSelectorDialog
        open={openDynamicFieldSelector}
        onClose={() => {
          setSelectedDynamicField(null);
          setOpenDynamicFieldSelector(false);
        }}
        onCreated={onFieldAdded}
        title={t('components.dynamic-field-selector.title')}
        value={selectedDynamicField}
        titleOnly
      />
    </Dialog>
  );
}

